import { createSelector } from "@reduxjs/toolkit";
import { IRootState } from "../app/store";
import { selectCompCodePath } from "./designSlice";
import {
  getValidationsForSpace,
  ISpaceValidation,
} from "../RoomSideBar/SpaceValidations/spaceValidationsUtils";
import {
  selectActiveMcbimSpace,
  selectHttpMcBimItemsInStandardSpace,
} from "../McBim/mcbimSelectors";
import {
  selectActiveRoomHasStandardRoom,
  selectActiveSingleRoomSummary,
  selectMlDevicesAvailableInSelectedSpace,
} from "../Room/roomSelectors";
import { IRoomSummary } from "@backend/src/schemas";
import { openapi } from "../../openapi";
import { selectProjectNo } from "../projectSelectors";
import { sortArrayByNumbers, sortArrayByStrings } from "../globalUtils";
import { RoomSideBarTabs } from "./designEnums";
import { ROOM_SIDEBAR_TABS_BY_MODULE } from "./GlobalDesignBar/designModules";
import { ac } from "vitest/dist/chunks/reporters.nr4dxCkA.js";
import { selectHttpDevicesInRoom } from "./devices/deviceSlice";

export const selectModRotation = (state: IRootState) => {
  const review = state.design.review;
  const rotation = state.design.rotation;
  return review.active === true ? 0 : rotation;
};

export const selectActiveColorFilter = (state: IRootState) => state.design.activeColorPanelItem;

const selectActiveSpaceValidationSpecs = (state: IRootState) =>
  state.design.activeSpaceValidationSpecs;

export const selectSpaceValidationForSelectedSpace = createSelector(
  [
    selectActiveSingleRoomSummary,
    selectActiveMcbimSpace,
    selectHttpDevicesInRoom,
    selectHttpMcBimItemsInStandardSpace,
    selectActiveSpaceValidationSpecs,
    selectCompCodePath,
  ],
  (
    room,
    mcbimSpace,
    devicesInRoom,
    mcbimItemsInStandardSpace,
    activeSpaceValidationSpecs,
    compCodePath
  ) => {
    if (mcbimSpace == null) {
      return [];
    }
    // Här skulle vi kunna memoiza getValidationsForSpace för bättre prestanda
    return getValidationsForSpace({
      devicesInRoom,
      singleRoom: room as IRoomSummary,
      mcbimItemsInStandardSpace,
      singleSpace: mcbimSpace,
      activeSpaceValidationSpecs,
      compCodePath: compCodePath as string,
    });
  }
);

export const selectActiveSpaceHasNoWarnings = createSelector(
  [selectSpaceValidationForSelectedSpace],
  (validations): boolean => {
    return validations.every((validation: ISpaceValidation) => validation.status === "VALID");
  }
);

const selectActiveModuleSelector = (state: IRootState) => state.design.activeModule;
const selectProjectTab = (state: IRootState) => state.global.activeProjectTab;

export const selectRoomSideBarTabs = createSelector(
  [
    selectActiveModuleSelector,
    selectMlDevicesAvailableInSelectedSpace,
    selectActiveRoomHasStandardRoom,
    selectProjectTab,
  ],
  (activeModule, roomHasMlPredictedDevices, roomHasStandardRoom, projectTab): RoomSideBarTabs[] => {
    // Samla alla tabs från alla moduler (utan duplikat)
    const allTabsAcrossModules: RoomSideBarTabs[] = Array.from(
      new Set(Object.values(ROOM_SIDEBAR_TABS_BY_MODULE).flat())
    );

    // Specialfall: projectTab === "room" → visa alla tabs oavsett modul
    if (projectTab === "room") {
      let tabs = allTabsAcrossModules;

      // Filtrera bort ML-tab om inga ML-förslag
      if (!roomHasMlPredictedDevices) {
        tabs = tabs.filter((tab) => tab !== RoomSideBarTabs.CEILING_AI_PREDICTIONS);
      }

      // Filtrera bort standard room-tab om rummet saknar standard room
      if (!roomHasStandardRoom) {
        tabs = tabs.filter((tab) => tab !== RoomSideBarTabs.CEILING_STANDARD_ROOM);
      }

      return tabs;
    }

    // Vanligt läge: styrs av aktiv modul
    if (!activeModule) {
      // No active module – show nothing
      return [];
    }

    let tabsForModule = ROOM_SIDEBAR_TABS_BY_MODULE[activeModule] ?? [];

    if (activeModule === "CEILING") {
      // Hide ML tab if no ML predictions for the room
      if (!roomHasMlPredictedDevices) {
        tabsForModule = tabsForModule.filter(
          (tab) => tab !== RoomSideBarTabs.CEILING_AI_PREDICTIONS
        );
      }

      // Hide standard room tab if the room has no standard room
      if (!roomHasStandardRoom) {
        tabsForModule = tabsForModule.filter(
          (tab) => tab !== RoomSideBarTabs.CEILING_STANDARD_ROOM
        );
      }
    }

    return tabsForModule;
  }
);

export const selectHttpMcbimBuildingStories = createSelector(
  [selectProjectNo, (state: IRootState) => state],
  (projectNo, state) => {
    if (!projectNo) {
      return [];
    }
    return (
      openapi.endpoints.getMcbimBuildingStories
        .select({ projectNo })(state)
        .data?.map((d, index) => {
          return {
            index: index + 1,
            ...d,
          };
        }) ?? []
    );
  }
);

const selectBuildingStoryListState = (state: IRootState) => state.design.buildingStoryList;

export const selectBuildingStoryList = createSelector(
  [selectHttpMcbimBuildingStories, selectBuildingStoryListState],
  (buildingStories, buildingStoryListState) => {
    const { search, sortMode } = buildingStoryListState;

    const source = buildingStories;

    // 1) filtrering
    const q = search.trim().toLowerCase();
    const filtered = q ? source.filter((story) => story.name.toLowerCase().includes(q)) : source;

    // 2) sortering (alltid på en kopia)
    const sorted = filtered.slice();

    if (sortMode === "name") {
      return sortArrayByStrings(sorted, ["name"]);
    } else {
      return sortArrayByNumbers(sorted, ["spaceCount"], "desc");
    }
  }
);

